import { UserClaims } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import notifications from "@/app/container/notifications";
import { useAppContext } from "@/app/context";
import { LinkedRegistry, RegistryLinkStatus } from "@/app/types/generated/graphql";
import { CustomUserClaims, EnabledRegistries, TraceXSupportedRegistries } from "@/app/types/okta.types";

import { UseOktaEnabledRegistries } from "./use-okta-enabled-registries-watch.types";

export const areEnabledRegistriesStale = (
  linkedRegistries?: LinkedRegistry[],
  enabledRegistries?: EnabledRegistries | null,
) => {
  if (!linkedRegistries) return false;

  return linkedRegistries.some((registry) => {
    if (registry.status !== RegistryLinkStatus.Registered || !registry.registryCode) return false;

    if (!enabledRegistries) return true;

    return !enabledRegistries[registry.registryCode as TraceXSupportedRegistries];
  });
};

export const useOktaEnabledRegistriesWatch: UseOktaEnabledRegistries = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const { userAccountInfo } = useAppContext();
  const [retryCount, setRetryCount] = useState(0);

  const enabledRegistriesFromClaim = useMemo(() => {
    if (!authState?.isAuthenticated) return null;

    const { enabledRegistries: enabledRegistriesStringified } =
      (authState?.accessToken?.claims as UserClaims<CustomUserClaims>) ?? {};

    const enabledRegistries = enabledRegistriesStringified
      ? (JSON.parse(enabledRegistriesStringified) as EnabledRegistries)
      : null;

    return enabledRegistries;
  }, [authState?.isAuthenticated, authState?.accessToken?.claims]);

  const cachedEnabledRegistries = useRef(enabledRegistriesFromClaim);

  useEffect(() => {
    if (!isEqual(cachedEnabledRegistries.current, enabledRegistriesFromClaim)) {
      cachedEnabledRegistries.current = enabledRegistriesFromClaim;
    }
  }, [enabledRegistriesFromClaim]);

  useEffect(() => {
    setRetryCount(0);
  }, [userAccountInfo?.linkedRegistries]);

  const refreshToken = useCallback(() => {
    setTimeout(async () => {
      try {
        await oktaAuth.tokenManager.renew("accessToken");
      } catch (e) {
        notifications.error({
          description: "Failed to refresh token",
        });
      } finally {
        setRetryCount((retryCount) => retryCount + 1);
      }
    }, 3000);
  }, [oktaAuth.tokenManager]);

  useEffect(() => {
    if (!authState?.isAuthenticated || retryCount >= 3) return;

    const requireEnabledRegistriesRefetch = areEnabledRegistriesStale(
      userAccountInfo?.linkedRegistries,
      cachedEnabledRegistries.current,
    );

    if (requireEnabledRegistriesRefetch) {
      refreshToken();
    }
  }, [retryCount, authState?.isAuthenticated, refreshToken, userAccountInfo?.linkedRegistries]);

  return { enabledRegistries: cachedEnabledRegistries.current };
};
